import type { ProductKind } from '@iheartjane/types';
import { ProductKindEnum } from '@iheartjane/types';
import cloneDeep from 'lodash-es/cloneDeep';
import { useCallback } from 'react';

import type { AlgoliaFacets, FilterSelectItem } from '@jane/search/types';
import {
  formatActiveFilters,
  handleFilterChange,
  overrideCustomCategoryLabels,
} from '@jane/search/util';
import { useGetStore } from '@jane/shared/data-access';
import type { Store } from '@jane/shared/types';

import type { RootLoaderType } from '../../../data-access';
import { useTypedRouteLoaderData } from '../../../hooks';
import { useSearchContext } from '../../../providers';
import type { MenuCategory } from '../../../types';
import { getSubcategories } from '../../../util';
import { QuickFilterButton } from '../QuickFilterButton/QuickFilterButton';

interface SubcategoryQuickFilterProps {
  category: MenuCategory;
  filteredProductCount: number;
  presentedFacets?: AlgoliaFacets;
  totalProductCount: number;
}

export const SubcategoryQuickFilter = ({
  category,
  filteredProductCount,
  presentedFacets,
  totalProductCount,
}: SubcategoryQuickFilterProps) => {
  const { store: initialStore, storeId } =
    useTypedRouteLoaderData<RootLoaderType>('root');
  const { data: store } = useGetStore<Store>(storeId, {
    initialData: initialStore,
  });
  const subcategories = getSubcategories(presentedFacets ?? {}, store);
  const showSubcategories = ProductKindEnum.includes(category as ProductKind);
  const subcategoryOptions =
    (showSubcategories && subcategories[category as ProductKind]) || [];
  const { resetSearchState, searchState, setSearchState } = useSearchContext();

  const onClearAllQuickFilters = useCallback(() => {
    const newState = cloneDeep(searchState);
    if (newState.filters?.root_types) {
      newState.filters.root_types = [];
    }

    resetSearchState(newState);
  }, [resetSearchState, searchState]);

  const onSubcategorySelected = useCallback(
    (option: FilterSelectItem) => {
      return setSearchState(
        handleFilterChange('root_types', option.value, searchState)
      );
    },
    [searchState, setSearchState]
  );

  const activeRootTypeFilters = formatActiveFilters(searchState, {
    customLabels: {
      category: store?.custom_lineage_labels,
      root_types: overrideCustomCategoryLabels(
        store?.state === 'California',
        store?.custom_product_type_labels
      ),
    },
    storeId: store?.id,
  }).filter(({ key }) => key === 'root_types');

  if (!showSubcategories) {
    return null;
  }

  return (
    <QuickFilterButton
      activeFilters={activeRootTypeFilters}
      label="Subcategory"
      onClearAll={onClearAllQuickFilters}
      onOptionSelect={onSubcategorySelected}
      options={subcategoryOptions}
      filteredProductCount={filteredProductCount}
      totalProductCount={totalProductCount}
      store={store}
    />
  );
};
